'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import type { LmsDataWithAdsType } from '@/app/api/lms/type';
import { LmsDataWithAdsSchema } from '@/app/api/lms/type';
import { AVAILABLE_TIME_OPTIONS } from '@/app/constants/register';
import { useGtmReady } from '@/app/hooks/useGtmReady';
import { BASE_FORM_ERROR_MESSAGE } from '@/app/lib/forms/utils/messages';
import { sanitizeFullnameInput } from '@/app/lib/forms/utils/validations.function';
import { getAdsTracking, getSkipCallCenter } from '@/app/utils/lms';
import { REGEX_ONLY_NUMBER } from '@/components/shared/utility/regex';
const FULLNAME_NAME = 'fullname';
const MOBILE_NAME = 'input-mobile';
const AVAILABLE_TIME_NAME = 'availableTime';
export const FULLNAME_PROPS = {
  name: FULLNAME_NAME,
  label: 'ชื่อ - นามสกุล',
  maxLength: 50,
  placeholder: 'กรอกชื่อ - นามสกุล',
  required: true,
  errorMessage: BASE_FORM_ERROR_MESSAGE.th
} as const;
export const MOBILE_PROPS = {
  id: MOBILE_NAME,
  // NOTE: use with tagmanager DOMELM_Lead_Mobile
  type: 'tel',
  name: MOBILE_NAME,
  maxLength: 10,
  label: 'เบอร์โทรศัพท์',
  placeholder: 'กรอกเบอร์ของท่าน',
  required: true,
  errorMessage: 'กรุณากรอกข้อมูลให้ครบถ้วน',
  'data-form-type': 'other' // NOTE: [TISW-10581] To prevent Dashlane extension popup that cause overflow layout bug
} as const;
export const AVAILABLE_TIME_PROPS = {
  label: 'เวลาที่สะดวกให้ติดต่อ',
  name: AVAILABLE_TIME_NAME,
  hidePlaceholder: true,
  options: AVAILABLE_TIME_OPTIONS
};
const defaultValue: LmsDataWithAdsType = {
  ctsCampaignId: '',
  ctsCampaignName: '',
  ctsCampaignVersion: '1',
  branchCode: '',
  firstName: '',
  mobile: '',
  model: 'pickup',
  availableTime: AVAILABLE_TIME_OPTIONS[0].value,
  lineId: '',
  email: '',
  remark: ''
};
interface LmsDataWithAdsTypeParams extends Omit<LmsDataWithAdsType, 'firstName' | 'mobile'> {
  firstName?: string;
  mobile?: string;
}
export const useLmsData = value => {
  const $ = _c(16);
  const [fullname, setFullname] = useState("");
  let t0;
  if ($[0] !== value) {
    t0 = {
      ...defaultValue,
      ...value
    };
    $[0] = value;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [lmsData, setLmsData] = useState(t0);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [lmsError, setLmsError] = useState(t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [manualErrors, setManualErrors] = useState(t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setLmsData(_temp);
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useGtmReady(t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (name, value_0) => {
      switch (name as string) {
        case FULLNAME_NAME:
          {
            const sanitizedValue = sanitizeFullnameInput(value_0);
            if (value_0 === "") {
              setFullname("");
              setLmsData(_temp2);
              setManualErrors(_temp3);
              return;
            }
            if (!sanitizedValue) {
              return;
            }
            setFullname(sanitizedValue);
            setLmsData(prev_3 => ({
              ...prev_3,
              firstName: sanitizedValue
            }));
            if (!sanitizedValue.includes(" ")) {
              setManualErrors(_temp4);
            } else {
              setManualErrors(_temp5);
            }
            return;
          }
        case MOBILE_NAME:
          {
            if (!REGEX_ONLY_NUMBER.test(value_0)) {
              return;
            }
            setLmsData(prev_0 => ({
              ...prev_0,
              mobile: value_0
            }));
            return;
          }
      }
      setLmsData(prev_6 => ({
        ...prev_6,
        [name]: value_0
      }));
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const onUpdateLms = t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setFullname("");
      setLmsData(_temp6);
      setManualErrors({});
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const reset = t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setManualErrors({});
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const clearManualErrors = t6;
  let t7;
  let t8;
  if ($[8] !== lmsData || $[9] !== manualErrors) {
    t7 = () => {
      const result = LmsDataWithAdsSchema.safeParse(lmsData);
      const schemaErrors = result.success ? {} : Object.fromEntries(result.error.issues.map(_temp7));
      setLmsError({
        ...schemaErrors,
        ...manualErrors
      });
    };
    t8 = [lmsData, manualErrors];
    $[8] = lmsData;
    $[9] = manualErrors;
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  useEffect(t7, t8);
  let t9;
  if ($[12] !== fullname || $[13] !== lmsData || $[14] !== lmsError) {
    t9 = {
      lmsData,
      lmsError,
      fullname,
      onUpdateLms,
      reset,
      clearManualErrors
    };
    $[12] = fullname;
    $[13] = lmsData;
    $[14] = lmsError;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
};
function _temp(prev) {
  return {
    ...prev,
    ...getAdsTracking(),
    ...getSkipCallCenter()
  };
}
function _temp2(prev_1) {
  return {
    ...prev_1,
    firstName: ""
  };
}
function _temp3(prev_2) {
  const {
    firstName,
    ...rest
  } = prev_2;
  return rest;
}
function _temp4(prev_4) {
  return {
    ...prev_4,
    firstName: ""
  };
}
function _temp5(prev_5) {
  const {
    firstName: firstName_0,
    ...rest_0
  } = prev_5;
  return rest_0;
}
function _temp6(prevLmsData) {
  return {
    ...prevLmsData,
    ...{
      branchCode: "",
      firstName: "",
      mobile: ""
    }
  };
}
function _temp7(t0) {
  const {
    path,
    message
  } = t0;
  return [path[0], message];
}